import { lazy, Suspense, useEffect, useMemo, useRef, useState } from "react";
import { ChevronLeft, ChevronRight, Check, Send } from "lucide-react";
import { Button } from "@/components/ui/button";
import api from "@/lib/api";

const Loader3DSymbol = lazy(() => import("@/components/Loader3DSymbol"));

const ACTIVITIES = [
  { code: "auto_locksmith", label: "Serrurerie automobile" },
  { code: "detailing", label: "Nettoyage / detailing automobile" },
  { code: "tint", label: "Pose de films teintés" },
  { code: "garage", label: "Garage / entretien automobile" },
  { code: "other", label: "Autre activité" },
];

const GOALS = [
  { code: "collect", label: "Recueillir les informations nécessaires aux demandes" },
  { code: "quote", label: "Préparer et envoyer des devis" },
  { code: "book", label: "Permettre la réservation d'un créneau" },
  { code: "other", label: "Autre besoin" },
];

const PRICING = [
  { code: "fixed", label: "Des forfaits fixes" },
  { code: "grid", label: "Une grille tarifaire selon plusieurs critères" },
  { code: "case", label: "Au cas par cas, après étude" },
  { code: "help", label: "Je souhaite être accompagné pour les structurer" },
];

const WEBSITE = [
  { code: "yes", label: "Oui" },
  { code: "no", label: "Non" },
  { code: "in_progress", label: "En cours de création" },
];

const emptyForm = {
  activity: "",
  activity_other: "",
  goals: [],
  goals_other: "",
  pricing_style: "",
  has_website: "",
  website_url: "",
  name: "",
  email: "",
  company: "",
  notes: "",
  hp: "", // honeypot
};

function RadioCard({ selected, onClick, children, testid }) {
  return (
    <button
      type="button"
      role="radio"
      aria-checked={selected}
      onClick={onClick}
      data-selected={selected ? "true" : "false"}
      className="kf-select-card !p-3 flex items-center justify-between text-left w-full"
      data-testid={testid}
    >
      <span className="text-sm font-semibold text-[#16324F]">{children}</span>
      <span
        className={`inline-flex items-center justify-center w-5 h-5 rounded-full border-2 transition-all duration-200 ${
          selected ? "bg-[#19C3B3] border-[#19C3B3] text-white scale-100" : "border-slate-200 text-transparent scale-90"
        }`}
        aria-hidden="true"
      >
        <Check className="w-3 h-3" strokeWidth={3} />
      </span>
    </button>
  );
}

function CheckCard({ selected, onClick, children, testid }) {
  return (
    <button
      type="button"
      role="checkbox"
      aria-checked={selected}
      onClick={onClick}
      data-selected={selected ? "true" : "false"}
      className="kf-select-card !p-3 flex items-center justify-between text-left w-full"
      data-testid={testid}
    >
      <span className="text-sm font-semibold text-[#16324F]">{children}</span>
      <span
        className={`inline-flex items-center justify-center w-5 h-5 rounded-md border-2 transition-all duration-200 ${
          selected ? "bg-[#19C3B3] border-[#19C3B3] text-white" : "border-slate-200 text-transparent"
        }`}
        aria-hidden="true"
      >
        <Check className="w-3 h-3" strokeWidth={3} />
      </span>
    </button>
  );
}

function Field({ label, children, required, hint, testid }) {
  return (
    <label className="block" data-testid={testid}>
      <span className="text-sm font-semibold text-[#16324F]">
        {label}{required && <span className="text-[#FF7A59]"> *</span>}
      </span>
      {hint && <span className="block text-xs text-slate-500 mt-0.5">{hint}</span>}
      <div className="mt-1.5">{children}</div>
    </label>
  );
}

function TextInput(props) {
  return (
    <input
      {...props}
      className={`w-full h-11 px-3 rounded-xl border border-slate-200 bg-white text-sm text-[#16324F] placeholder:text-slate-400 focus:outline-none focus:border-[#19C3B3] focus:ring-2 focus:ring-[#19C3B3]/20 ${props.className || ""}`}
    />
  );
}

function TextArea(props) {
  return (
    <textarea
      {...props}
      className={`w-full min-h-24 px-3 py-2 rounded-xl border border-slate-200 bg-white text-sm text-[#16324F] placeholder:text-slate-400 focus:outline-none focus:border-[#19C3B3] focus:ring-2 focus:ring-[#19C3B3]/20 ${props.className || ""}`}
    />
  );
}

const STEPS = 5;

function labelFor(list, code) {
  return list.find((x) => x.code === code)?.label || code;
}

export default function MtgLeadForm() {
  const [step, setStep] = useState(1);
  const [form, setForm] = useState(emptyForm);
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [showLoader, setShowLoader] = useState(false);
  const [progressMsg, setProgressMsg] = useState("");
  const [result, setResult] = useState(null); // { lead_id, mode, email_sent_to_prospect, admin_notified }
  const [partner, setPartner] = useState(false);
  const loaderTimer = useRef(null);

  useEffect(() => () => { if (loaderTimer.current) clearTimeout(loaderTimer.current); }, []);

  // Demande de partenariat prescripteur : on identifie la demande via une note
  // (canal de contact existant réutilisé, suivi manuel côté admin).
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get("sujet") === "partenaire") {
      setPartner(true);
      setForm((f) => (f.notes ? f : { ...f, notes: "[Demande de partenariat prescripteur] " }));
    }
  }, []);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const toggleGoal = (code) => setForm((f) => ({
    ...f, goals: f.goals.includes(code) ? f.goals.filter((c) => c !== code) : [...f.goals, code],
  }));

  const canNext = useMemo(() => {
    if (step === 1) {
      if (!form.activity) return false;
      if (form.activity === "other" && !form.activity_other.trim()) return false;
      return true;
    }
    if (step === 2) {
      if (form.goals.length === 0) return false;
      if (form.goals.includes("other") && !form.goals_other.trim()) return false;
      return true;
    }
    if (step === 3) return !!form.pricing_style;
    if (step === 4) return !!form.has_website;
    if (step === 5) {
      return !!form.name.trim() && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email);
    }
    return true;
  }, [step, form]);

  const goPrev = () => { setError(""); setStep((s) => Math.max(1, s - 1)); };
  const goNext = () => { setError(""); setStep((s) => Math.min(STEPS, s + 1)); };

  async function submit() {
    setError("");
    setSubmitting(true);
    setProgressMsg("Transmission de votre demande…");
    // Show 3D loader only if the operation is slower than 300 ms — avoids flash.
    if (loaderTimer.current) clearTimeout(loaderTimer.current);
    loaderTimer.current = setTimeout(() => setShowLoader(true), 300);
    try {
      const payload = {
        activity: form.activity,
        activity_other: form.activity === "other" ? form.activity_other.trim() : null,
        goals: form.goals,
        goals_other: form.goals.includes("other") ? form.goals_other.trim() : null,
        pricing_style: form.pricing_style,
        has_website: form.has_website,
        website_url: form.has_website === "yes" ? (form.website_url || "").trim() : null,
        name: form.name.trim(),
        email: form.email.trim(),
        company: form.company.trim() || null,
        notes: form.notes.trim() || null,
        hp: form.hp || "",
      };
      const r = await api.post("/public/mtg-leads", payload);
      setResult(r.data);
    } catch (e) {
      const msg = e?.response?.data?.detail || "L'enregistrement a échoué. Vérifiez votre connexion puis réessayez.";
      setError(typeof msg === "string" ? msg : "L'enregistrement a échoué.");
    } finally {
      if (loaderTimer.current) { clearTimeout(loaderTimer.current); loaderTimer.current = null; }
      setShowLoader(false);
      setSubmitting(false);
      setProgressMsg("");
    }
  }

  // --- Success card -------------------------------------------------
  if (result) {
    const studyMode = result.mode !== "quote";
    return (
      <div className="bg-white rounded-3xl border border-slate-100 shadow-[0_30px_80px_-40px_rgba(16,50,79,0.35)] p-6 md:p-8" data-testid="mtg-lead-success">
        <div className="inline-flex items-center gap-2 rounded-full bg-[#19C3B3]/10 text-[#0E5A56] text-xs font-bold px-3 py-1">
          <Check className="w-3 h-3" strokeWidth={3} /> Demande transmise
        </div>
        <h3 className="mt-4 font-display text-2xl font-extrabold text-[#16324F]">
          {studyMode
            ? "Merci ! Votre projet a bien été transmis."
            : "Merci ! Votre proposition est disponible."}
        </h3>
        <p className="mt-2 text-sm text-slate-600 leading-relaxed">
          {studyMode
            ? "Nous allons étudier vos besoins et revenir vers vous pour préciser la solution et son tarif."
            : "Votre devis a été préparé selon les critères saisis."}
        </p>

        <div className="mt-6 rounded-2xl bg-[#F6F9FC] border border-[#EAF1F7] p-4">
          <div className="text-xs font-bold uppercase tracking-widest text-slate-500">Récapitulatif</div>
          <dl className="mt-3 grid gap-2 text-sm">
            <div className="flex justify-between gap-4"><dt className="text-slate-500">Métier</dt><dd className="font-semibold text-[#16324F] text-right">{form.activity === "other" ? form.activity_other : labelFor(ACTIVITIES, form.activity)}</dd></div>
            <div className="flex justify-between gap-4"><dt className="text-slate-500">Automatisations</dt><dd className="font-semibold text-[#16324F] text-right">{form.goals.filter((c) => c !== "other").map((c) => labelFor(GOALS, c)).concat(form.goals.includes("other") ? [form.goals_other] : []).join(" · ") || "—"}</dd></div>
            <div className="flex justify-between gap-4"><dt className="text-slate-500">Tarification</dt><dd className="font-semibold text-[#16324F] text-right">{labelFor(PRICING, form.pricing_style)}</dd></div>
            <div className="flex justify-between gap-4"><dt className="text-slate-500">Site internet</dt><dd className="font-semibold text-[#16324F] text-right">{labelFor(WEBSITE, form.has_website)}{form.website_url ? ` · ${form.website_url}` : ""}</dd></div>
            <div className="flex justify-between gap-4"><dt className="text-slate-500">Contact</dt><dd className="font-semibold text-[#16324F] text-right">{form.name} · {form.email}{form.company ? ` · ${form.company}` : ""}</dd></div>
          </dl>
        </div>

        <div className="mt-4 text-xs text-slate-500">
          {result.email_sent_to_prospect
            ? "Un récapitulatif vous a été envoyé par e-mail."
            : "Nous n'avons pas pu confirmer l'envoi du récapitulatif par e-mail. Nous vous recontacterons directement."}
        </div>
      </div>
    );
  }

  // --- Form ---------------------------------------------------------
  const pct = Math.round(((step - 1) / STEPS) * 100);
  return (
    <div className="bg-white rounded-3xl border border-slate-100 shadow-[0_30px_80px_-40px_rgba(16,50,79,0.35)] p-6 md:p-8" data-testid="mtg-lead-form">
      {/* Honeypot */}
      <input
        type="text"
        name="website_confirm"
        value={form.hp}
        onChange={(e) => set("hp", e.target.value)}
        tabIndex={-1}
        autoComplete="off"
        aria-hidden="true"
        style={{ position: "absolute", left: "-10000px", width: 1, height: 1, opacity: 0 }}
      />

      <div className="flex items-center justify-between gap-3">
        <div className="text-xs font-semibold text-slate-400" data-testid="mtg-step-indicator">Étape {step} sur {STEPS}</div>
        <div className="text-xs text-slate-400">Environ 2 minutes</div>
      </div>
      {partner && (
        <div className="mt-3 rounded-xl bg-[#FF7A59]/10 border border-[#FF7A59]/30 text-[#8A2E14] text-xs font-semibold px-3 py-2" data-testid="mtg-partner-banner">
          Demande de partenariat prescripteur
        </div>
      )}
      <div className="kf-progress-track mt-2"><div className="kf-progress-fill" style={{ width: `${Math.max(pct, 10)}%` }}></div></div>

      <div className="mt-6 min-h-[280px]">
        {step === 1 && (
          <div>
            <h3 className="font-display text-2xl font-bold text-[#16324F]">Quel est votre métier ?</h3>
            <div className="mt-4 grid gap-2" role="radiogroup" aria-label="Votre métier">
              {ACTIVITIES.map((a) => (
                <RadioCard key={a.code} selected={form.activity === a.code} onClick={() => set("activity", a.code)} testid={`mtg-activity-${a.code}`}>
                  {a.label}
                </RadioCard>
              ))}
            </div>
            {form.activity === "other" && (
              <div className="mt-4">
                <Field label="Précisez votre activité" required testid="mtg-activity-other-field">
                  <TextInput value={form.activity_other} onChange={(e) => set("activity_other", e.target.value)} placeholder="Ex : réparation de pare-brise" data-testid="mtg-activity-other" />
                </Field>
              </div>
            )}
          </div>
        )}

        {step === 2 && (
          <div>
            <h3 className="font-display text-2xl font-bold text-[#16324F]">Que souhaitez-vous automatiser ?</h3>
            <p className="mt-1 text-sm text-slate-500">Sélectionnez au moins une réponse.</p>
            <div className="mt-4 grid gap-2" role="group" aria-label="Vos besoins d'automatisation">
              {GOALS.map((g) => (
                <CheckCard key={g.code} selected={form.goals.includes(g.code)} onClick={() => toggleGoal(g.code)} testid={`mtg-goal-${g.code}`}>
                  {g.label}
                </CheckCard>
              ))}
            </div>
            {form.goals.includes("other") && (
              <div className="mt-4">
                <Field label="Précisez votre besoin" required testid="mtg-goal-other-field">
                  <TextInput value={form.goals_other} onChange={(e) => set("goals_other", e.target.value)} placeholder="Ex : rappels de rendez-vous" data-testid="mtg-goal-other" />
                </Field>
              </div>
            )}
          </div>
        )}

        {step === 3 && (
          <div>
            <h3 className="font-display text-2xl font-bold text-[#16324F]">Comment fixez-vous vos tarifs ?</h3>
            <div className="mt-4 grid gap-2" role="radiogroup" aria-label="Structure tarifaire">
              {PRICING.map((p) => (
                <RadioCard key={p.code} selected={form.pricing_style === p.code} onClick={() => set("pricing_style", p.code)} testid={`mtg-pricing-${p.code}`}>
                  {p.label}
                </RadioCard>
              ))}
            </div>
          </div>
        )}

        {step === 4 && (
          <div>
            <h3 className="font-display text-2xl font-bold text-[#16324F]">Avez-vous déjà un site internet ?</h3>
            <div className="mt-4 grid gap-2" role="radiogroup" aria-label="Site internet">
              {WEBSITE.map((w) => (
                <RadioCard key={w.code} selected={form.has_website === w.code} onClick={() => set("has_website", w.code)} testid={`mtg-website-${w.code}`}>
                  {w.label}
                </RadioCard>
              ))}
            </div>
            {form.has_website === "yes" && (
              <div className="mt-4">
                <Field label="Adresse de votre site" hint="Facultatif" testid="mtg-website-url-field">
                  <TextInput type="url" inputMode="url" value={form.website_url} onChange={(e) => set("website_url", e.target.value)} placeholder="https://…" data-testid="mtg-website-url" />
                </Field>
              </div>
            )}
          </div>
        )}

        {step === 5 && (
          <div>
            <h3 className="font-display text-2xl font-bold text-[#16324F]">Comment vous recontacter ?</h3>
            <div className="mt-4 grid gap-4">
              <Field label="Nom" required testid="mtg-name-field">
                <TextInput value={form.name} onChange={(e) => set("name", e.target.value)} autoComplete="name" data-testid="mtg-name" />
              </Field>
              <Field label="Adresse e-mail" required testid="mtg-email-field">
                <TextInput type="email" value={form.email} onChange={(e) => set("email", e.target.value)} autoComplete="email" data-testid="mtg-email" />
              </Field>
              <Field label="Nom de l'entreprise" hint="Facultatif" testid="mtg-company-field">
                <TextInput value={form.company} onChange={(e) => set("company", e.target.value)} autoComplete="organization" data-testid="mtg-company" />
              </Field>
              <Field label="Précisions sur votre projet" hint="Facultatif" testid="mtg-notes-field">
                <TextArea value={form.notes} onChange={(e) => set("notes", e.target.value)} placeholder="Volume de demandes, spécificités, contraintes…" data-testid="mtg-notes" />
              </Field>
            </div>

            {/* Recap */}
            <div className="mt-6 rounded-2xl bg-[#F6F9FC] border border-[#EAF1F7] p-4">
              <div className="text-xs font-bold uppercase tracking-widest text-slate-500">Récapitulatif de vos réponses</div>
              <dl className="mt-3 grid gap-1.5 text-sm">
                <div className="flex justify-between gap-4"><dt className="text-slate-500">Métier</dt><dd className="font-semibold text-[#16324F] text-right">{form.activity === "other" ? (form.activity_other || "—") : labelFor(ACTIVITIES, form.activity)}</dd></div>
                <div className="flex justify-between gap-4"><dt className="text-slate-500">Automatisations</dt><dd className="font-semibold text-[#16324F] text-right">{form.goals.filter((c) => c !== "other").map((c) => labelFor(GOALS, c)).concat(form.goals.includes("other") ? [form.goals_other || "Autre"] : []).join(" · ") || "—"}</dd></div>
                <div className="flex justify-between gap-4"><dt className="text-slate-500">Tarification</dt><dd className="font-semibold text-[#16324F] text-right">{labelFor(PRICING, form.pricing_style)}</dd></div>
                <div className="flex justify-between gap-4"><dt className="text-slate-500">Site internet</dt><dd className="font-semibold text-[#16324F] text-right">{labelFor(WEBSITE, form.has_website)}{form.website_url ? ` · ${form.website_url}` : ""}</dd></div>
              </dl>
            </div>

            <p className="mt-4 text-xs text-slate-500 leading-relaxed">
              Si votre besoin correspond à une offre standard disponible, vous recevez un devis. Sinon, nous vous recontactons pour préciser votre projet et le chiffrer.
            </p>
            <p className="mt-3 text-xs text-slate-500 leading-relaxed" data-testid="mtg-privacy-note">
              En envoyant ce formulaire, vos informations sont traitées par FlamingOps pour répondre à votre demande.{" "}
              <a href="/confidentialite" target="_blank" rel="noopener noreferrer" className="underline underline-offset-2 text-[#16324F]">Confidentialité</a>.
            </p>
          </div>
        )}
      </div>

      {error && (
        <div className="mt-4 rounded-xl bg-[#FF7A59]/10 border border-[#FF7A59]/30 text-[#8A2E14] text-sm px-4 py-3" data-testid="mtg-error">
          {error}
        </div>
      )}

      {/* Progress + loader row (shown during submit) */}
      {(submitting || progressMsg) && (
        <div className="mt-4 flex items-center gap-3" role="status" aria-live="polite" data-testid="mtg-progress">
          {showLoader && (
            <Suspense fallback={<img src="/brand/mtg_symbol.png" alt="" aria-hidden="true" style={{ width: 48, height: 48 }} />}>
              <Loader3DSymbol size={48} />
            </Suspense>
          )}
          <span className="text-sm font-semibold text-[#16324F]">{progressMsg || "Transmission de votre demande…"}</span>
        </div>
      )}

      <div className="mt-6 flex items-center justify-between gap-3">
        <Button
          type="button"
          variant="outline"
          className="rounded-full border-[#16324F]/20 text-[#16324F] h-11"
          onClick={goPrev}
          disabled={step === 1 || submitting}
          data-testid="mtg-prev"
        >
          <ChevronLeft className="w-4 h-4 mr-1" /> Précédent
        </Button>

        {step < STEPS ? (
          <Button
            type="button"
            className="rounded-full bg-[#16324F] hover:bg-[#0F2438] text-white h-11 px-5"
            onClick={goNext}
            disabled={!canNext || submitting}
            data-testid="mtg-next"
          >
            Suivant <ChevronRight className="w-4 h-4 ml-1" />
          </Button>
        ) : (
          <Button
            type="button"
            className="rounded-full bg-[#19C3B3] hover:bg-[#0FAB9B] text-[#16324F] h-11 px-5 font-bold"
            onClick={submit}
            disabled={!canNext || submitting}
            data-testid="mtg-submit"
          >
            <Send className="w-4 h-4 mr-2" /> Recevoir ma proposition
          </Button>
        )}
      </div>
    </div>
  );
}
